<template>
    <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
        <ControlsMenuComponent class="sm:mb-6" />

        <div class="w-full grid grid-cols-2 col-span-12 gap-x-4 mt-3 sm:mt-0">
            <div class="overflow-hidden relative rounded-lg bg-white shadow-sm col-span-2 sm:col-span-1">
                <div v-if="isUpdated" class="fixed z-10 bottom-5 right-0 left-0 w-full flex items-center justify-center text-white text-sm">
                    <div class="bg-stone-800/90 backdrop-filter backdrop-blur-lg rounded-full shadow-md px-4 py-3 inline-flex items-center gap-2">
                        <svg class="shrink-0 size-4 text-teal-500" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"></path>
                        </svg>
                        <span>{{ $t('gallery_design.changes_saved') }}</span>
                    </div>
                </div>

                <div class="px-4 py-5 sm:p-6">
                    <div>
                        <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_design.title') }}</h3>
                        <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_design.description') }}</p>
                    </div>

                    <div class="grid grid-cols-6 gap-6 mt-3" v-if="form.design">
                        <div class="col-span-full grid grid-cols-4 gap-6">
                            <div class="col-span-4">
                                <label for="cover-photo" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_design.cover') }}</label>
                                <CoverUploaderComponent
                                    :position="form.cover.position"
                                    :cover_url="form.cover.uploaded ? form.cover.url : null"
                                    :preview_url="cover.preview_url"
                                    :is_file_selected="cover.file !== null"
                                    :is_file_uploading="cover.is_uploading"
                                    v-if="form.cover"
                                    :allow-video="true"
                                    @positionUpdated="coverPositionsUpdated"
                                    @positionUpdating="coverPositionsUpdating"
                                    @coverFileSelected="selectCoverFile"
                                />
                            </div>
                        </div>

                        <div class="col-span-6">
                            <div class="flex gap-3">
                                <div class="flex h-6 shrink-0 items-center">
                                    <div class="group grid size-4 grid-cols-1">
                                        <input
                                            id="title_color"
                                            aria-describedby="title_color-description"
                                            name="title_color"
                                            type="color"
                                            v-model="form.design.title_color"
                                            class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-royal-blue-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                        />
                                        <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                            <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div class="text-sm/6">
                                    <label for="title_color" class="font-medium text-gray-900">{{ $t('gallery_design.title_color') }}</label>
                                </div>
                            </div>
                        </div>

                        <div class="col-span-6" v-if="gallery.date">
                            <div class="flex gap-3">
                                <div class="flex h-6 shrink-0 items-center">
                                    <div class="group grid size-4 grid-cols-1">
                                        <input
                                            id="subtitle_color"
                                            aria-describedby="subtitle_color-description"
                                            name="subtitle_color"
                                            type="color"
                                            v-model="form.design.subtitle_color"
                                            class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-royal-blue-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                        />
                                    </div>
                                </div>
                                <div class="text-sm/6">
                                    <label for="subtitle_color" class="font-medium text-gray-900">{{ $t('gallery_design.subtitle_color') }}</label>
                                </div>
                            </div>
                        </div>

                        <div class="col-span-6">
                            <SwitchGroup as="div" class="flex items-center justify-between">
                                <Switch v-model="form.design.show_title" :class="[form.design.show_title ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                    <span aria-hidden="true" :class="[form.design.show_title ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                                </Switch>
                                <span class="flex grow flex-col ms-4">
                                    <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>{{ $t('gallery_design.show_title') }}</SwitchLabel>
                                    <SwitchDescription as="span" class="text-sm text-gray-500">{{ $t('gallery_design.show_title_description') }}</SwitchDescription>
                                </span>
                            </SwitchGroup>
                        </div>

                        <div class="col-span-6" v-if="form.date">
                            <SwitchGroup as="div" class="flex items-center justify-between">
                                <Switch v-model="form.design.show_subtitle" :class="[form.design.show_subtitle ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                    <span aria-hidden="true" :class="[form.design.show_subtitle ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                                </Switch>
                                <span class="flex grow flex-col ms-4">
                                    <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>{{ $t('gallery_design.show_subtitle') }}</SwitchLabel>
                                    <SwitchDescription as="span" class="text-sm text-gray-500">{{ $t('gallery_design.show_subtitle_description') }}</SwitchDescription>
                                </span>
                            </SwitchGroup>
                        </div>

                        <div class="col-span-6">
                            <Listbox as="div" v-model="form.design.title_font">
                                <ListboxLabel class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_design.title_font') }}</ListboxLabel>
                                <div class="relative mt-2">
                                    <ListboxButton class="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6">
                                        <span :class="['col-start-1 row-start-1 truncate pr-6', 'font-item', form.design.title_font]">{{ fonts[form.design.title_font] }}</span>
                                        <ChevronUpDownIcon class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                    </ListboxButton>

                                    <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                        <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
                                            <ListboxOption as="template" v-for="(title, key) in fonts" :key="key" :value="key">
                                                <li :class="[key == form.design.title_font ? 'bg-royal-blue-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default py-2 pr-9 pl-3 select-none']">
                                                    <span :class="[key == form.design.title_font ? 'font-semibold' : 'font-normal', 'block truncate', 'font-item', key]">{{ title }}</span>

                                                    <span v-if="key == form.design.title_font" :class="[key == form.design.title_font ? 'text-white' : 'text-royal-blue-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                        <CheckIcon class="size-5" aria-hidden="true" />
                                                    </span>
                                                </li>
                                            </ListboxOption>
                                        </ListboxOptions>
                                    </transition>
                                </div>
                            </Listbox>
                        </div>

                        <div class="col-span-6" v-if="gallery.date">
                            <Listbox as="div" v-model="form.design.subtitle_font">
                                <ListboxLabel class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_design.subtitle_font') }}</ListboxLabel>
                                <div class="relative mt-2">
                                    <ListboxButton class="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6">
                                        <span :class="['col-start-1 row-start-1 truncate pr-6', 'font-item', form.design.subtitle_font]">{{ fonts[form.design.subtitle_font] }}</span>
                                        <ChevronUpDownIcon class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                    </ListboxButton>

                                    <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                        <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
                                            <ListboxOption as="template" v-for="(title, key) in fonts" :key="key" :value="key">
                                                <li :class="[key == form.design.subtitle_font ? 'bg-royal-blue-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default py-2 pr-9 pl-3 select-none']">
                                                    <span :class="[key == form.design.subtitle_font ? 'font-semibold' : 'font-normal', 'block truncate', 'font-item', key]">{{ title }}</span>

                                                    <span v-if="key == form.design.subtitle_font" :class="[key == form.design.subtitle_font ? 'text-white' : 'text-royal-blue-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                        <CheckIcon class="size-5" aria-hidden="true" />
                                                    </span>
                                                </li>
                                            </ListboxOption>
                                        </ListboxOptions>
                                    </transition>
                                </div>
                            </Listbox>
                        </div>

                        <div class="col-span-6">
                            <div class="w-full mb-3">
                                <label class="block mb-2 font-medium"><label class="block mb-2 font-medium">{{ $t('gallery_design.cover_design') }}</label></label>
                                <div class="w-full">
                                    <div class="flex gap-2" role="group" :aria-label="$t('gallery_design.cover_design')">
                                        <template v-for="cover in coverTypes" :key="cover.value">
                                            <input type="radio" name="cover_type" v-model="form.design.cover_type" :id="cover.value" :value="cover.value" class="hidden">
                                            <label :for="cover.value" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.cover_type == cover.value}">
                                                <img :src="cover.img" :alt="cover.label" class="w-16 h-16 object-cover rounded-md">
                                            </label>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-span-6 rounded-md border border-gray-200">
                            <div class="flex border-b border-gray-200">
                                <button
                                    type="button"
                                    @click.prevent="isDesktop = true"
                                    :class="{'w-full flex justify-center items-center gap-2 cursor-pointer text-center border-b-2 px-4 py-4 text-sm font-medium whitespace-nowrap': true, 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700': !isDesktop, 'border-royal-blue-500 text-royal-blue-600': isDesktop}">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pc-display" viewBox="0 0 16 16">
                                        <path d="M8 1a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1zm1 13.5a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0m2 0a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0M9.5 1a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM9 3.5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 0-1h-5a.5.5 0 0 0-.5.5M1.5 2A1.5 1.5 0 0 0 0 3.5v7A1.5 1.5 0 0 0 1.5 12H6v2h-.5a.5.5 0 0 0 0 1H7v-4H1.5a.5.5 0 0 1-.5-.5v-7a.5.5 0 0 1 .5-.5H7V2z"/>
                                    </svg>

                                    <span class="hidden sm:block">{{ $t('gallery_design.desktop_version') }}</span>
                                </button>

                                <button
                                    type="button"
                                    @click.prevent="isDesktop = false"
                                    :class="{'w-full flex justify-center items-center gap-2 cursor-pointer text-center border-b-2 px-4 py-4 text-sm font-medium whitespace-nowrap': true, 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700': isDesktop, 'border-royal-blue-500 text-royal-blue-600': !isDesktop}">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-phone" viewBox="0 0 16 16">
                                        <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                                        <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2"/>
                                    </svg>

                                    <span class="hidden sm:block">{{ $t('gallery_design.mobile_version') }}</span>
                                </button>
                            </div>
                            <div class="grid grid-cols-6 gap-6 p-3">
                                <div class="col-span-6" v-if="isDesktop">
                                    <SwitchGroup as="div" class="flex items-center justify-between">
                                        <Switch v-model="form.design.is_rounded_photos" :class="[form.design.is_rounded_photos ? 'bg-royal-blue-500' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-500 focus:ring-offset-2 focus:outline-hidden']">
                                            <span aria-hidden="true" :class="[form.design.is_rounded_photos ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                                        </Switch>
                                        <span class="flex grow flex-col ms-4">
                                            <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>{{ $t('gallery_design.rounded_photos') }}</SwitchLabel>
                                            <SwitchDescription as="span" class="text-sm text-gray-500">{{ $t('gallery_design.rounded_photos_description') }}</SwitchDescription>
                                        </span>
                                    </SwitchGroup>
                                </div>

                                <div class="col-span-6" v-if="!isDesktop">
                                    <SwitchGroup as="div" class="flex items-center justify-between">
                                        <Switch v-model="form.design.is_rounded_photos_mobile" :class="[form.design.is_rounded_photos_mobile ? 'bg-royal-blue-500' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-500 focus:ring-offset-2 focus:outline-hidden']">
                                            <span aria-hidden="true" :class="[form.design.is_rounded_photos_mobile ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                                        </Switch>
                                        <span class="flex grow flex-col ms-4">
                                            <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>{{ $t('gallery_design.rounded_photos') }}</SwitchLabel>
                                            <SwitchDescription as="span" class="text-sm text-gray-500">{{ $t('gallery_design.rounded_photos_description') }}</SwitchDescription>
                                        </span>
                                    </SwitchGroup>
                                </div>

                                <div class="col-span-6" v-if="['without', 'full'].includes(form.design.cover_type) && isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.title_position') }}</label>
                                        <div class="grid grid-cols-3 gap-3">
                                            <template v-for="item in ['top-left','top-center','top-right','center-left','center','center-right','bottom-left','bottom-center','bottom-right']" :key="`title_cover_position-item-${item}`">
                                                <input type="radio" name="title_cover_position" v-model="form.design.title_cover_position" :id="`title_cover_position-input-${item}`" :value="item" class="hidden">
                                                <label :for="`title_cover_position-input-${item}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.title_cover_position == item}">
                                                    <img :src="require(`@/assets/images/gallery-design/title-position/${item}.png`)" :alt="item" class="w-full object-cover">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="['small', 'small-without'].includes(form.design.cover_type) && isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.title_position') }}</label>
                                        <div class="grid grid-cols-3 gap-3">
                                            <template v-for="item in ['center-left','center','center-right']" :key="`title_cover_position-item-${item}`">
                                                <input type="radio" name="title_cover_position" v-model="form.design.title_cover_position" :id="`title_cover_position-input-${item}`" :value="item" class="hidden">
                                                <label :for="`title_cover_position-input-${item}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.title_cover_position == item}">
                                                    <img :src="require(`@/assets/images/gallery-design/title-position/${item}.png`)" :alt="item" class="w-full object-cover">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="['without', 'full'].includes(form.design.cover_type) && !isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.title_position') }}</label>
                                        <div class="grid grid-cols-3 gap-3">
                                            <template v-for="item in ['top-left','top-center','top-right','center-left','center','center-right','bottom-left','bottom-center','bottom-right']" :key="`title_cover_position-item-${item}`">
                                                <input type="radio" name="title_cover_position" v-model="form.design.title_cover_position_mobile" :id="`title_cover_position-input-${item}`" :value="item" class="hidden">
                                                <label :for="`title_cover_position-input-${item}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.title_cover_position_mobile == item}">
                                                    <img :src="require(`@/assets/images/gallery-design/title-position/${item}.png`)" :alt="item" class="w-full object-cover">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="['small', 'small-without'].includes(form.design.cover_type) && !isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.title_position') }}</label>
                                        <div class="grid grid-cols-3 gap-3">
                                            <template v-for="item in ['center-left','center','center-right']" :key="`title_cover_position-item-${item}`">
                                                <input type="radio" name="title_cover_position" v-model="form.design.title_cover_position_mobile" :id="`title_cover_position-input-${item}`" :value="item" class="hidden">
                                                <label :for="`title_cover_position-input-${item}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.title_cover_position_mobile == item}">
                                                    <img :src="require(`@/assets/images/gallery-design/title-position/${item}.png`)" :alt="item" class="w-full object-cover">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="isDesktop">
                                    <div class="w-full mb-3">
                                        <div class="grid grid-cols-1 gap-4">
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.title_font_size') }} - <span class="text-gray-500">{{ form.design.title_font_size }}</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="10"
                                                    max="180"
                                                    step="5"
                                                    v-model="form.design.title_font_size"
                                                    id="title_font_size"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${((form.design.title_font_size - 10) / 170) * 100}% - 1%), #e5e7eb calc(${((form.design.title_font_size - 10) / 170) * 100}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.subtitle_font_size') }} - <span class="text-gray-500">{{ form.design.subtitle_font_size }}</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="5"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.subtitle_font_size"
                                                    id="subtitle_font_size"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${((form.design.subtitle_font_size - 5) / 95 * 100)}% - 1%), #e5e7eb calc(${((form.design.subtitle_font_size - 5) / 95 * 100)}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="!isDesktop">
                                    <div class="w-full mb-3">
                                        <div class="grid grid-cols-1 gap-4">
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.title_font_size') }} - <span class="text-gray-500">{{ form.design.title_font_size_mobile }}</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="10"
                                                    max="180"
                                                    step="5"
                                                    v-model="form.design.title_font_size_mobile"
                                                    id="title_font_size_mobile"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${((form.design.title_font_size_mobile - 10) / 170) * 100}% - 1%), #e5e7eb calc(${((form.design.title_font_size_mobile - 10) / 170) * 100}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.subtitle_font_size') }} - <span class="text-gray-500">{{ form.design.subtitle_font_size_mobile }}</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="5"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.subtitle_font_size_mobile"
                                                    id="subtitle_font_size_mobile"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${((form.design.subtitle_font_size_mobile - 5) / 95 * 100)}% - 1%), #e5e7eb calc(${((form.design.subtitle_font_size_mobile - 5) / 95 * 100)}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="!['without'].includes(form.design.cover_type) && isDesktop">
                                    <div class="w-full mb-3">
                                        <div class="grid grid-cols-1 gap-4">
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.gradient_opacity') }} - <span class="text-gray-500">{{ 100 - form.design.gradient_opacity_percent }}%</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.gradient_opacity_percent"
                                                    id="gradient_opacity_percent"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${form.design.gradient_opacity_percent}% - 1%), #e5e7eb calc(${form.design.gradient_opacity_percent}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.gradient_fill') }} - <span class="text-gray-500">{{ form.design.gradient_fill_percent }}%</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.gradient_fill_percent"
                                                    id="gradient_fill_percent"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${form.design.gradient_fill_percent}% - 1%), #e5e7eb calc(${form.design.gradient_fill_percent}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="!['without'].includes(form.design.cover_type) && !isDesktop">
                                    <div class="w-full mb-3">
                                        <div class="grid grid-cols-1 gap-4">
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.gradient_opacity') }} - <span class="text-gray-500">{{ 100 - form.design.gradient_opacity_percent_mobile }}%</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.gradient_opacity_percent_mobile"
                                                    id="gradient_opacity_percent_mobile"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${form.design.gradient_opacity_percent_mobile}% - 1%), #e5e7eb calc(${form.design.gradient_opacity_percent_mobile}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                            <div>
                                                <label class="block mb-1 text-sm font-medium">
                                                    {{ $t('gallery_design.gradient_fill') }} - <span class="text-gray-500">{{ form.design.gradient_fill_percent_mobile }}%</span>
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    v-model="form.design.gradient_fill_percent_mobile"
                                                    id="gradient_fill_percent_mobile"
                                                    class="w-full appearance-none h-2 rounded-lg"
                                                    :style="{
                                                      background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${form.design.gradient_fill_percent_mobile}% - 1%), #e5e7eb calc(${form.design.gradient_fill_percent_mobile}% + 1%), #e5e7eb 100%)`
                                                    }"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="rounded-md bg-royal-blue-50 p-3 shadow-xs" v-if="isDesktop">
                                        <div class="flex">
                                            <div class="shrink-0">
                                                <InformationCircleIcon class="size-5 text-royal-blue-500" aria-hidden="true" />
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-royal-blue-500">{{ $t('gallery_design.info') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.photos_per_row') }}</label>
                                        <div class="flex gap-2" role="group" :aria-label="$t('gallery_design.photos_per_row')">
                                            <template v-for="layout in layouts" :key="layout.value">
                                                <input type="radio" name="layout" v-model="form.design.layout" @change="reloadFrames" :id="`layout_${layout.value}`" :value="layout.value" class="hidden">
                                                <label :for="`layout_${layout.value}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.layout == layout.value}">
                                                    <img :src="require(`@/assets/images/gallery-design/layout-${layout.value}.png`)" :alt="layout.value" class="w-12 h-12 object-cover rounded-md">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="!isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-3 font-medium">{{ $t('gallery_design.photos_per_row') }}</label>
                                        <div class="flex gap-2" role="group" :aria-label="$t('gallery_design.photos_per_row')">
                                            <template v-for="layout in layouts" :key="layout.value">
                                                <input type="radio" name="layout" v-model="form.design.layout_mobile" @change="reloadFrames" :id="`layout_${layout.value}`" :value="layout.value" class="hidden">
                                                <label :for="`layout_${layout.value}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.layout_mobile == layout.value}">
                                                    <img :src="require(`@/assets/images/gallery-design/layout-${layout.value}.png`)" :alt="layout.value" class="w-12 h-12 object-cover rounded-md">
                                                </label>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-1 font-medium">
                                            {{ $t('gallery_design.photo_spacing') }} -
                                            <span class="text-gray-500" v-if="form.design.padding == 0">{{ $t('gallery_design.no_spacing') }}</span>
                                            <span class="text-gray-500" v-else>{{ form.design.padding }}</span>
                                        </label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            step="1"
                                            v-model="form.design.padding"
                                            id="padding"
                                            class="w-full appearance-none h-2 rounded-lg"
                                            :style="{
                                        background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${(form.design.padding / 50) * 100}% - 1%), #e5e7eb calc(${(form.design.padding / 50) * 100}% + 1%), #e5e7eb 100%)`
                                    }"
                                        >
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="!isDesktop">
                                    <div class="w-full mb-3">
                                        <label class="block mb-1 font-medium">
                                            {{ $t('gallery_design.photo_spacing') }} -
                                            <span class="text-gray-500" v-if="form.design.padding_mobile == 0">{{ $t('gallery_design.no_spacing') }}</span>
                                            <span class="text-gray-500" v-else>{{ form.design.padding_mobile }}</span>
                                        </label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            step="1"
                                            v-model="form.design.padding_mobile"
                                            id="padding_mobile"
                                            class="w-full appearance-none h-2 rounded-lg"
                                            :style="{
                                        background: `linear-gradient(to right, #2563eb 0%, #2563eb calc(${(form.design.padding_mobile / 50) * 100}% - 1%), #e5e7eb calc(${(form.design.padding_mobile / 50) * 100}% + 1%), #e5e7eb 100%)`
                                    }"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-span-6">
                            <div class="w-full mb-3">
                                <label class="block mb-1 font-medium">{{ $t('gallery_design.theme') }}</label>
                                <div class="flex gap-2" role="group" :aria-label="$t('gallery_design.theme')">
                                    <template v-for="theme in themes" :key="theme.value">
                                        <input type="radio" name="theme" v-model="form.design.theme" @change="reloadFrames" :id="`theme_${theme.value}`" :value="theme.value" class="hidden">
                                        <label :for="`theme_${theme.value}`" :class="{'cursor-pointer rounded-md hover:scale-105 transition duration-300': true, 'border-blue-670 ring-2 ring-blue-700 scale-105': form.design.theme == theme.value}">
                                            <img :src="require(`@/assets/images/gallery-design/theme-${theme.value}.png`)" :alt="theme.value" class="w-16 h-16 object-cover rounded-md">
                                        </label>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-span-1 hidden sm:block">
                <GalleryFrameComponent v-if="isCurrentGallery" :frame-url="gallery.frame_url" ref="gallery-frame" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {mapActions, mapGetters} from "vuex";

import {Gallery} from "@/types/GalleryType";
import {ResponseError} from "@/types";
import {validationMethods} from "@/_helpers/validation";
import {updateCover, updateDesign} from "@/data/requests/gallery";

import ControlsMenuComponent from "@/components/Galleries/ControlsMenuComponent.vue";
import CoverUploaderComponent from "@/components/Galleries/CoverUploaderComponent.vue";
import GalleryFrameComponent from "@/components/Galleries/GalleryFramePreviewComponent.vue";

import { Switch, SwitchDescription, SwitchGroup, SwitchLabel,
    Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/16/solid'
import {CheckIcon, InformationCircleIcon} from '@heroicons/vue/20/solid'

export default defineComponent({
    name: "DesignPage",

    components: {
        InformationCircleIcon,
        GalleryFrameComponent,
        CoverUploaderComponent,
        ControlsMenuComponent,

        ChevronUpDownIcon,
        CheckIcon,

        Switch, SwitchDescription, SwitchGroup, SwitchLabel,
        Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions
    },

    data() {
        return {
            isLoadingGallery: false,

            form: {} as Gallery,

            updated: null,

            isUpdated: false,
            isUpdating: false,

            errors: {} as { [key: string]: string[] },

            cover: {
                file: null as File | null,
                preview_url: null as string | null,
                is_uploading: false,
            },

            isDesktop: true,

            fonts: {
                'default': this.$t('gallery_design.default_font'),
                'forum-font': 'Forum',
                'marck-script-font': 'Marck Script',
                'tenor-sans-font': 'Tenor Sans',
                'montserrat-font': 'Montserrat',
                'roboto-font': 'Roboto',
                'poiret-one-regular-font': 'Poiret One',
                'cormorant-unicase-light-font': 'Cormorant Unicase',
                'alumni-sans-pinstripe-regular-font': 'Alumni Sans Pinstripe',
                'arsenal-sc-regular-font': 'Arsenal SC Regular',
                'unbounded-font': 'Unbounded',
                'great-vibes-font': 'Great Vibes',
                'cormorant-garamond-font': 'Cormorant Garamond',
                'pacifico-font': 'Pacifico',
            },

            coverTypes: [
                {
                    value: 'full',
                    label: 'Заповнено',
                    img: require('@/assets/images/gallery-design/cover-full.png'),
                },
                {
                    value: 'half',
                    label: 'Наполовину',
                    img: require('@/assets/images/gallery-design/cover-half.png'),
                },
                {
                    value: 'half_right',
                    label: 'Наполовину Праворуч',
                    img: require('@/assets/images/gallery-design/cover-half-right.png'),
                },
                {
                    value: 'small-without',
                    label: 'Маленька без обкладинки',
                    img: require('@/assets/images/gallery-design/cover-small-without.png'),
                },
                {
                    value: 'small',
                    label: 'Маленька',
                    img: require('@/assets/images/gallery-design/cover-small.png'),
                },
                {
                    value: 'without',
                    label: 'Без обкладинки',
                    img: require('@/assets/images/gallery-design/cover-none.png'),
                },
                {
                    value: 'none',
                    label: 'Без обкладинки і заголовку',
                    img: require('@/assets/images/gallery-design/cover-none-new.png'),
                }
            ],

            layouts: [
                {
                    value: 1,
                    label: '1',
                    img: require('@/assets/images/gallery-design/layout-1.png'),
                },
                {
                    value: 2,
                    label: '2',
                    img: require('@/assets/images/gallery-design/layout-2.png'),
                },
                {
                    value: 3,
                    label: '3',
                    img: require('@/assets/images/gallery-design/layout-3.png'),
                },
                {
                    value: 4,
                    label: '4',
                    img: require('@/assets/images/gallery-design/layout-4.png'),
                },
                {
                    value: 5,
                    label: '5',
                    img: require('@/assets/images/gallery-design/layout-5.png'),
                }
            ],

            themes: [
                {
                    value: 'light',
                    label: 'Світла',
                    img: require('@/assets/images/gallery-design/theme-light.png'),
                },
                {
                    value: 'dark',
                    label: 'Темна',
                    img: require('@/assets/images/gallery-design/theme-dark.png'),
                },
                {
                    value: 'beige',
                    label: 'Бежева',
                    img: require('@/assets/images/gallery-design/theme-beige.png'),
                }
            ]
        };
    },

    watch: {
        form: {
            deep: true,
            handler() {
                this.errors = {};
                this.isUpdated = false;

                this.updateFrames({});

                this.updateChanges();
            }
        },
        isUpdated(newValue) {
            if(newValue) {
                setTimeout(() => {
                    this.isUpdated = false;
                }, 3000);
            }
        },
        'form.design.theme': function (newValue, oldValue) {
            if(oldValue !== undefined) {
                this.submit();
            }
        }
    },

    computed: {
        ...mapGetters({
            gallery: 'galleries/gallery'
        }),

        currentSlug(): string {
            return this.$route.params.gallery as string;
        },

        isCurrentGallery(): boolean {
            return this.gallery != null && this.gallery.slug == this.currentSlug;
        }
    },

    mounted() {
        if(this.isCurrentGallery) {
            this.form = { ...this.gallery };

            return;
        }

        this.fetchGallery();
    },

    methods: {
        ...mapActions({
            loadGallery: "galleries/loadGallery",

            errorAlert: "alerts/error"
        }),

        ...validationMethods(),

        fetchGallery(slug: string | null = null) {
            if(!this.isCurrentGallery) {
                this.isLoadingGallery = true;
            }

            if(slug == null) {
                slug = this.currentSlug;
            }

            this.loadGallery({slug})
                .then((gallery: Gallery) => {
                    this.form = { ...gallery };

                    this.isLoadingGallery = false;
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('gallery_design.fetch_error', {message: data.message}),
                    });

                    this.isLoadingGallery = false;
                });
        },

        submit() {
            this.updateChanges(true);
        },

        updateChanges(refresh: boolean = false) {
            if(this.isUpdating) {
                return;
            }

            this.isUpdating = true;

            updateDesign(this.gallery.slug, this.form, () => {
                this.isUpdated = true;
                this.isUpdating = false;

                if(refresh) {
                    this.reloadFrames();
                }
            }, (type, data) => {
                this.isUpdating = false;

                if (type == ResponseError.Validation) {
                    this.errors = data.data;

                    this.errorAlert({
                        message: this.$t('gallery_design.validation_error')
                    });
                } else {
                    this.errorAlert({
                        message: data.message,
                    });
                }
            })
        },

        selectCoverFile(file: File | null) {
            delete this.errors['cover'];

            this.cover.file = file;

            if(this.cover.file) {
                this.cover.preview_url = URL.createObjectURL(this.cover.file);

                this.cover.is_uploading = true;

                updateCover(this.gallery.slug, this.cover.file, (data) => {
                    this.form.cover.url = data.data.url;
                    this.form.cover.uploaded = true;
                    this.cover.file = null;
                    this.cover.preview_url = null;

                    this.cover.is_uploading = false;

                    this.reloadFrames();
                }, (type, data) => {
                    this.cover.file = null;
                    this.cover.preview_url = null;

                    if (type == ResponseError.Validation) {
                        this.errors = data.data;
                    }

                    this.errorAlert({
                        message: data?.message ?? this.$t('gallery_design.cover_error'),
                    });

                    this.cover.is_uploading = false;
                })
            }
        },

        coverPositionsUpdated(positions: {x: number, y: number}) {
            this.form.cover.position = positions;
        },

        coverPositionsUpdating(positions: {x: number, y: number}) {
            this.updateFrames({position: {...positions}});
        },

        updateFrames(data: Object) {
            (this.$refs['gallery-frame'] as any).updateFrames({
                ...data,
                ...this.form.design
            });
        },

        reloadFrames() {
            setTimeout(() => {
                (this.$refs['gallery-frame'] as any).reloadFrames();
            }, 1000);
        },
    }
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Forum&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Marck+Script&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Tenor+Sans&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Montserrat&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Poiret+One&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Unicase:wght@300&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Alumni+Sans+Pinstripe:ital@0;1&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Arsenal+SC&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@200..900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital@0;1&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Pacifico&display=swap');

input[type="color"] {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
}

input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
}

input[type="color"]::-webkit-color-swatch {
    border: none;
}

.design-editor-button-group {
    bottom: 20px;
    position: sticky;
}

.loader {
    display: block;
    margin: 6.53px auto;
    width: 10px;
    aspect-ratio: 1;
    border-radius: 50%;
    animation: l5 1s infinite linear alternate;
}

@keyframes l5 {
    0%  {box-shadow: 20px 0 #fff, -20px 0 #ccc;background: #fff }
    33% {box-shadow: 20px 0 #fff, -20px 0 #ccc;background: #ccc}
    66% {box-shadow: 20px 0 #ccc,-20px 0 #fff; background: #ccc}
    100%{box-shadow: 20px 0 #ccc,-20px 0 #fff; background: #fff }
}

.font-item {
    &.forum-font {
        font-family: 'Forum', cursive;
    }

    &.marck-script-font {
        font-family: 'Marck Script', cursive;
    }

    &.tenor-sans-font {
        font-family: 'Tenor Sans', sans-serif;
    }

    &.syne-font {
        font-family: 'Syne', sans-serif;
        font-weight: 700;
    }

    &.montserrat-font {
        font-family: 'Montserrat', sans-serif;
    }

    &.roboto-font {
        font-family: 'Roboto Mono', monospace;
    }

    &.poiret-one-regular-font {
        font-family: "Poiret One", sans-serif;
        font-weight: 400;
        font-style: normal;
    }

    &.cormorant-unicase-light-font {
        font-family: "Cormorant Unicase", serif;
        font-weight: 300;
        font-style: normal;
    }

    &.alumni-sans-pinstripe-regular-font {
        font-family: "Alumni Sans Pinstripe", sans-serif;
        font-weight: 400;
        font-style: normal;
    }

    &.arsenal-sc-regular-font {
        font-family: "Arsenal SC", sans-serif;
        font-weight: 400;
        font-style: normal;
    }

    &.unbounded-font {
        font-family: "Unbounded", cursive;
        font-weight: 400;
        font-style: normal;
    }

    &.great-vibes-font {
        font-family: "Great Vibes", cursive;
        font-weight: 400;
        font-style: normal;
    }

    &.cormorant-garamond-font {
        font-family: "Cormorant Garamond", serif;
        font-weight: 400;
        font-style: normal;
    }

    &.pacifico-font {
        font-family: "Pacifico", cursive;
        font-weight: 400;
        font-style: normal;
    }
}
</style>